import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import { emptyMetrics, type AnalyticsFilter, type AnalyticsMetric, type InteractionRecord } from '@/shared/analytics/AnalyticsTypes';

const PROMPT_METRICS: AnalyticsMetric[] = ['turns', 'sessions', 'users', 'totalTokens', 'allocatedCostUsd'];
/** Projected so each record carries its fit score and the difficulty/model needed to compute waste. */
const DIMENSION_KEYS = ['model_fit', 'difficulty', 'user_department', 'task_category'];

/**
 * Loads the raw prompts (and a rolled-up summary) behind a Model Fit scope — the
 * per-user or per-model drill grid. Projects the model-fit score and difficulty
 * onto each record so the grid can show the fit score and per-prompt waste.
 */
@injectable()
export class BehaviorModelFitPromptsViewModel extends BaseViewModel {
    records: InteractionRecord[] = [];
    totalMatched = 0;
    summary: Record<AnalyticsMetric, number> = emptyMetrics();
    loading = false;
    errorMessage: string | null = null;

    constructor(@inject(IAnalyticsGatewayToken) private gateway: IAnalyticsGateway) {
        super();
    }

    async load(filters: AnalyticsFilter[]): Promise<void> {
        this.records = [];
        this.totalMatched = 0;
        this.summary = emptyMetrics();
        this.loading = true;
        this.errorMessage = null;
        this.notifyChange();
        try {
            const [recordsResult, aggregate] = await Promise.all([
                this.gateway.records({
                    source: 'interactions',
                    filters,
                    dimensionKeys: DIMENSION_KEYS,
                    orderBy: { column: 'timestamp', direction: 'desc' },
                }),
                this.gateway.aggregate({ source: 'interactions', groupBy: [], metrics: PROMPT_METRICS, filters }),
            ]);
            this.records = recordsResult.records;
            this.totalMatched = recordsResult.totalMatched;
            this.summary = aggregate.totals;
        } catch (err) {
            this.errorMessage = err instanceof Error ? err.message : String(err);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }
}
